import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { sublicense_management_urls } from '../../../utilities/services/Constants/api-constants';
import { BehaviorSubject, map, Observable, shareReplay } from 'rxjs';
import { RoleResponse } from '../../../models/Role/role.model';

export interface GetSubLicenseRolesOptions {
  role_ids?: number;
  page_num?: number;
  per_page?: number;
  join_verifiers?: boolean;
  join_parents?: boolean;
  role_fields?: boolean;
  is_active?: boolean;
  is_login?: boolean;
}

const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
  }),
  observe: 'response' as const,
};

const SUB_LICENSE_RETURN_CONTEXT_KEY = 'subLicenseReturnContext';
const SELECTED_ROLE_KEY = 'selectedSubLicenseRole';

export interface SubLicenseReturnContext {
  user_role_id: number;
  tab_index: number;
  event_tab_index: number;
}

@Injectable({
  providedIn: 'root'
})
export class SubLicenseManagementService {

  constructor(private readonly httpClient: HttpClient) { }


  private readonly subLicenseTabIndexSubject = new BehaviorSubject<number>(0);
  subLicenseTabIndex$ = this.subLicenseTabIndexSubject.asObservable();

  private readonly selectedRoleSubject = new BehaviorSubject<any>(null);
  selectedRole$ = this.selectedRoleSubject.asObservable();
  setSubLicenseTabIndex(index: number): void {
    this.subLicenseTabIndexSubject.next(index);
  }

  getSubLicenseTabIndex(): number {
    return this.subLicenseTabIndexSubject.getValue();
  }

  setSelectedRole(role: any): void {
    this.selectedRoleSubject.next(role);
    // Persist to sessionStorage
    if (sessionStorage !== undefined && role) {
      sessionStorage.setItem(SELECTED_ROLE_KEY, JSON.stringify(role));
    }
  }

  getSelectedRole(): any {
    // First check the BehaviorSubject
    let role = this.selectedRoleSubject.getValue();
    
    // If not found, try to get from sessionStorage
    if (!role && sessionStorage !== undefined) {
      try {
        const storedRole = sessionStorage.getItem(SELECTED_ROLE_KEY);
        if (storedRole) {
          role = JSON.parse(storedRole);
          this.selectedRoleSubject.next(role);
        }
      } catch (error) {
        console.error('Error parsing stored role:', error);
      }
    }
    
    return role;
  }

  clearSelectedRole(): void {
    this.selectedRoleSubject.next(null);
    if (sessionStorage !== undefined) {
      sessionStorage.removeItem(SELECTED_ROLE_KEY);
    }
  }

  setSubLicenseReturnContext(ctx: SubLicenseReturnContext): void {
    if (sessionStorage !== undefined) {
      sessionStorage.setItem(SUB_LICENSE_RETURN_CONTEXT_KEY, JSON.stringify(ctx));
    }
  }

  getSubLicenseReturnContext(): SubLicenseReturnContext | null {
    if (sessionStorage === undefined) return null;
    try {
      const raw = sessionStorage.getItem(SUB_LICENSE_RETURN_CONTEXT_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as SubLicenseReturnContext;
      if (parsed == null || typeof parsed.user_role_id !== 'number' || typeof parsed.tab_index !== 'number' || typeof parsed.event_tab_index !== 'number') {
        return null;
      }
      return parsed;
    } catch {
      return null;
    }
  }

  clearSubLicenseReturnContext(): void {
    if (sessionStorage !== undefined) {
      sessionStorage.removeItem(SUB_LICENSE_RETURN_CONTEXT_KEY);
    }
  }

  getUserProfileData(user_role_id: number) {
    return this.httpClient
      .get(
        sublicense_management_urls.get_user_profile_data +
        '?user_role_id=' +
        user_role_id,
        httpOptions
      )
      .pipe(
        shareReplay(),
        map((response: any) => {
          return {
            code: response.status,
            msg: response.body.msg,
            data: response.body.data,
          };
        })
      );
  }

  getRoles(options: GetSubLicenseRolesOptions = {}): Observable<RoleResponse> {
    const {
      role_ids = 0,
      page_num = 0,
      per_page = 0,
      join_verifiers = false,
      join_parents = false,
      role_fields = false,
      is_active = false,
      is_login = false,
    } = options;

    let params = new HttpParams();

    if (role_ids > 0) {
      params = params.set('role_ids', role_ids.toString());
    }
    if (page_num > 0) {
      params = params.set('page_num', page_num.toString());
    }
    if (per_page > 0) {
      params = params.set('per_page', per_page.toString());
    }
    if (join_verifiers) {
      params = params.set('join_verifiers', join_verifiers.toString());
    }
    if (join_parents) {
      params = params.set('join_parents', join_parents.toString());
    }
    if (role_fields) {
      params = params.set('role_fields', role_fields.toString());
    }
    if (is_active) {
      params = params.set('is_active', is_active.toString());
    }
    if (is_login) {
      params = params.set('is_login', is_login.toString());
    }

    return this.httpClient
      .get<RoleResponse>(sublicense_management_urls.get_sublicensee_roles_config, { params, ...httpOptions })
      .pipe(
        shareReplay(),
        map((response: any): RoleResponse => ({
          code: response.status,
          msg: response.body.msg,
          data: response.body.data,
          success: response.body.data,
        }))
      );
  }
}
